import {
  Handshake,
  Sliders,
  RocketLaunch,
  Medal,
  ChartLineUp,
  Infinity as InfinityIcon,
} from "@phosphor-icons/react";
import { Reveal, Overline } from "@/components/Reveal";

const LEADER_IMG =
  "https://images.unsplash.com/photo-1573496130141-209d200cebd8?auto=format&fit=crop&w=1200&q=80";

const CARDS = [
  {
    icon: Handshake,
    title: "Strategic Partnership",
    body: "We sit on your side of the table, thinking like owners, advising like executives, and staying for the long term.",
    span: "md:col-span-4",
    dark: false,
  },
  {
    icon: ChartLineUp,
    title: "Real Operational Impact",
    body: "Strategy that survives Monday morning. We measure success in smoother shifts, stronger teams, and numbers that move.",
    span: "md:col-span-2",
    dark: true,
  },
  {
    icon: Sliders,
    title: "Customized Solutions",
    body: "No recycled playbooks. Every engagement is built around your people, your pressures, and your stage of growth.",
    span: "md:col-span-2",
    dark: false,
  },
  {
    icon: RocketLaunch,
    title: "Practical Implementation",
    body: "We don't hand you a binder. We build, coach, and implement alongside your team until it works.",
    span: "md:col-span-2",
    dark: false,
  },
  {
    icon: Medal,
    title: "Leadership Expertise",
    body: "Senior-level HR and Operations leaders who have run the function, not just studied it.",
    span: "md:col-span-2",
    dark: false,
  },
  {
    icon: InfinityIcon,
    title: "Business-Focused, Long-Term Support",
    body: "People strategy tied to business outcomes, with a partner who knows your business deeply and grows with you.",
    span: "md:col-span-4",
    dark: false,
  },
];

const WhyUs = () => (
  <section id="why-us" data-testid="why-us-section" className="py-24 md:py-32 px-6 md:px-10">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
        <Reveal className="md:col-span-4">
          <Overline>Why Network in Colors</Overline>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-8">
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-tight text-ink max-w-2xl">
            Consulting that feels like{" "}
            <span className="font-serif italic font-medium text-terracotta">having us on the team.</span>
          </h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
        <Reveal className="md:col-span-2 md:row-span-2" delay={0.05}>
          <div className="relative overflow-hidden rounded-3xl h-64 md:h-full min-h-[20rem] group">
            <img
              src={LEADER_IMG}
              alt="Two confident businesswomen standing together in an office"
              data-testid="why-us-leader-image"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 font-serif italic text-white text-xl md:text-2xl leading-snug">
              "The right partner changes how the whole business feels."
            </p>
          </div>
        </Reveal>

        {CARDS.map((c, i) => (
          <Reveal key={c.title} className={c.span} delay={0.08 + i * 0.06}>
            <div
              data-testid={`why-us-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-3xl p-7 md:p-8 h-full transition-transform duration-500 hover:-translate-y-1.5 ${
                c.dark
                  ? "bg-ink text-white"
                  : "bg-cream text-ink shadow-[0_8px_30px_rgb(0,0,0,0.03)]"
              }`}
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 ${
                  c.dark ? "bg-terracotta text-white" : "bg-white text-terracotta shadow-sm"
                }`}
              >
                <c.icon size={24} weight="duotone" />
              </div>
              <h3 className="font-display font-bold tracking-tight text-xl md:text-2xl mb-3">
                {c.title}
              </h3>
              <p className={`text-sm md:text-base leading-relaxed ${c.dark ? "text-white/75" : "text-ink-soft"}`}>
                {c.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default WhyUs;
